import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedReport, ManagedReportSecurity, ManagedReportInput, ManagedReportPage } from '../Shared/ManagedReport';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminOperation,
    ManagedAdminEditorMode,
    ManagedActionModalMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    mapReportAdminModel,
 } from '../Shared/ManagedHelpers';
import Administration from '../../Constants/Endpoints/Administration';
import GetReportAdminModelsWithChildrenRequest from '../../Interfaces/Requests/Administration/GetReportAdminModelsWithChildrenRequest';
import GetReportAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelsWithChildrenResponse';
import AUDReportRequest from '../../Interfaces/Requests/Administration/AUDReportRequest';
import {
    AddIcon,
    Button,
    Card,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { CloseCircleOutlined } from '@ant-design/icons';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAIReportExplainModelRequest from '../../Interfaces/Requests/AI/GetAIReportExplainModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAIExplainModelResponse from '../../Interfaces/Responses/AI/GetAIExplainModelResponse';
import AUDReportSecurities, { ManagedReportSecurityEditResult } from './AUDReportSecurities';
import AUDReportInputs, { ManagedReportInputEditResult } from './AUDReportInputs';
import AUDReportPages, { ManagedReportPageEditResult } from './AUDReportPages';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';

type AUDReportProps = {};

const AUDReport: React.FC<AUDReportProps> = () => {
    const { user } = useAuth();
    const [formAUDReports] = Form.useForm();

    const queryClient = useQueryClient();

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({ open: false, mode: 'info', title: '', message: '', isError: false, });

    const [addButtonClicked, setAddButtonClicked] = useState(true);
    const [updButtonClicked, setUpdButtonClicked] = useState(false);
    const [delButtonClicked, setDelButtonClicked] = useState(false);

    const [rSelectedReportID, setRSelectedReportID] = useState(-1);
    const [rSelectedReportName, setRSelectedReportName] = useState('');

    const [rDetailsReportSecurities, setRDetailsReportSecurities] = useState<ManagedReportSecurity[]>([]);
    const [rDetailsReportInputs, setRDetailsReportInputs] = useState<ManagedReportInput[]>([]);
    const [rDetailsReportPages, setRDetailsReportPages] = useState<ManagedReportPage[]>([]);

    const [manageReportSecuritiesModalOpen, setManageReportSecuritiesModalOpen] = useState(false);
    const [manageReportInputsModalOpen, setManageReportInputsModalOpen] = useState(false);
    const [manageReportPagesModalOpen, setManageReportPagesModalOpen] = useState(false);

    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);

    const [rSelectedReportSecurityID, setRSelectedReportSecurityID] = useState(-1);
    const [rSelectedReportInputID, setRSelectedReportInputID] = useState(-1);
    const [rSelectedReportPageID, setRSelectedReportPageID] = useState(-1);
    const [rReportSecurityEditorMode, setRReportSecurityEditorMode] = useState<ManagedAdminActionMode>('idle');
    const [rReportInputEditorMode, setRReportInputEditorMode] = useState<ManagedAdminActionMode>('idle');
    const [rReportPageEditorMode, setRReportPageEditorMode] = useState<ManagedAdminActionMode>('idle');

    const [rDetailsName, setRDetailsName] = useState('');
    const [rDetailsGroupName, setRDetailsGroupName] = useState('');
    const [rDetailsNameGeneric, setRDetailsNameGeneric] = useState('');
    const [rDetailsGroupNameGeneric, setRDetailsGroupNameGeneric] = useState('');

    const [rIncludeDisabled] = useState(false);

    const [rSelectedReportCreatedBy, setRSelectedReportCreatedBy] = useState(-1);
    const [rSelectedReportCreatedTs, setRSelectedReportCreatedTs] = useState(new Date());
    const [rSelectedReportDeletedBy, setRSelectedReportDeletedBy] = useState(-1);
    const [rSelectedReportDeletedTs, setRSelectedReportDeletedTs] = useState(new Date());

    const showRInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const buildRErrorMessage = (prefix: string, err: unknown): string => {
        const apiError = err as ManagedApiError;

        return `${prefix}${apiError?.response?.data?.message ? ` ${apiError.response.data.message}` : ''}`;
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredRField = (fieldName: string) => {
        const fieldValue = formAUDReports.getFieldValue(fieldName);
        return isBlank(fieldValue) ? Promise.reject() : Promise.resolve();
    };

    const validateRDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'rDetailsName':
            case 'rDetailsGroupName':
            case 'rDetailsSort':
                return validateRequiredRField(rule.field);

            case 'rDetailsNameGeneric':
            case 'rDetailsGroupNameGeneric':
            case 'rDetailsExportPassword':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const setRMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getROperation = (): ManagedAdminOperation => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Upd';
        if (delButtonClicked) return 'Del';
        return '';
    };

    const getRActionModalMode = (): ManagedActionModalMode => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        if (delButtonClicked) return 'delete';
        return 'info';
    };

    const formatRDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const getRActionLabel = (): string => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Update';
        if (delButtonClicked) return 'Delete';
        return 'Manage';
    };

    const getRActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const getRActionTargetName = (): string => {
        return rSelectedReportName || formAUDReports.getFieldValue('rDetailsName') || '';
    };

    const showRResultModal = (isError: boolean, message: string) => {
        setManageActionModalState(buildManagedActionModalState(isError ? 'info' : getRActionModalMode(), `${getRActionLabel()} Report`, message, isError));
    };

    const clearRSelection = () => {
        setRSelectedReportID(-1);
        setRSelectedReportName('');
        setRSelectedReportCreatedBy(-1);
        setRSelectedReportCreatedTs(new Date());
        setRSelectedReportDeletedBy(-1);
        setRSelectedReportDeletedTs(new Date());
        setRSelectedReportSecurityID(-1);
        setRReportSecurityEditorMode('idle');
        setRSelectedReportInputID(-1);
        setRReportInputEditorMode('idle');
        setRSelectedReportPageID(-1);
        setRReportPageEditorMode('idle');
        setManageReportSecuritiesModalOpen(false);
        setManageReportInputsModalOpen(false);
        setManageReportPagesModalOpen(false);
        setRMode('add');
        setRBlankForm();
    };

    const buildAUDReportRequest = (): AUDReportRequest => {
        const rDetailsNameL = formAUDReports.getFieldValue('rDetailsName');
        const rDetailsGroupNameL = formAUDReports.getFieldValue('rDetailsGroupName');
        const rDetailsIsDashboardL = formAUDReports.getFieldValue('rDetailsIsDashboard');
        const rDetailsUseDatasetInputsL = formAUDReports.getFieldValue('rDetailsUseDatasetInputs');
        const rDetailsReleasedL = formAUDReports.getFieldValue('rDetailsReleased');
        const rDetailsNameGenericL = formAUDReports.getFieldValue('rDetailsNameGeneric');
        const rDetailsGroupNameGenericL = formAUDReports.getFieldValue('rDetailsGroupNameGeneric');
        const rDetailsSortL = formAUDReports.getFieldValue('rDetailsSort');
        const rDetailsAPIOnlyL = formAUDReports.getFieldValue('rDetailsAPIOnly');
        const rDetailsExportExcelL = formAUDReports.getFieldValue('rDetailsExportExcel');
        const rDetailsExportPDFL = formAUDReports.getFieldValue('rDetailsExportPDF');
        const rDetailsExportPasswordL = formAUDReports.getFieldValue('rDetailsExportPassword');

        return {
            id: rSelectedReportID,
            name: rDetailsNameL ?? '',
            groupName: rDetailsGroupNameL ?? '',
            isDashboard: rDetailsIsDashboardL ?? false,
            use_Dataset_Inputs: rDetailsUseDatasetInputsL ?? false,
            created_By: addButtonClicked ? (user?.id ?? 0) : rSelectedReportCreatedBy,
            modified_By: user?.id ?? 0,
            deleted_By: delButtonClicked ? (user?.id ?? 0) : rSelectedReportDeletedBy,
            created_Ts: addButtonClicked ? (new Date()) : rSelectedReportCreatedTs,
            modified_Ts: new Date(),
            deleted_Ts: delButtonClicked ? (new Date()) : rSelectedReportDeletedTs,
            released: rDetailsReleasedL ?? false,
            name_Generic: rDetailsNameGenericL ?? '',
            groupName_Generic: rDetailsGroupNameGenericL ?? '',
            sort: rDetailsSortL ?? 0,
            apiOnly: rDetailsAPIOnlyL ?? false,
            export_Excel: rDetailsExportExcelL ?? false,
            export_PDF: rDetailsExportPDFL ?? false,
            export_Password: rDetailsExportPasswordL ?? '',

            audReportSecurities: rDetailsReportSecurities.map(
                (eachReportSecurityElement) => {
                    return {
                        id: eachReportSecurityElement.id,
                        report_id: eachReportSecurityElement.reportId,
                        user_id: eachReportSecurityElement.userId,
                        tenant_id: eachReportSecurityElement.tenantId,
                        userRole_id: eachReportSecurityElement.userRoleId,
                        created_By: eachReportSecurityElement.createdBy,
                        modified_By: eachReportSecurityElement.modifiedBy,
                        deleted_By: eachReportSecurityElement.deletedBy,
                        created_Ts: eachReportSecurityElement.createdTs,
                        modified_Ts: eachReportSecurityElement.modifiedTs,
                        deleted_Ts: eachReportSecurityElement.deletedTs
                    }
                }
            ),

            audReportInputs: rDetailsReportInputs.map(
                (eachReportInputElement) => {
                    return {
                        id: eachReportInputElement.id,
                        report_id: eachReportInputElement.reportId,
                        title: eachReportInputElement.title,
                        sourceQuery: eachReportInputElement.sourceQuery,
                        multiSelect: eachReportInputElement.multiSelect,
                        selector: eachReportInputElement.selector,
                        dataFormat: eachReportInputElement.dataFormat,
                        required: eachReportInputElement.required,
                        created_By: eachReportInputElement.createdBy,
                        modified_By: eachReportInputElement.modifiedBy,
                        deleted_By: eachReportInputElement.deletedBy,
                        created_Ts: eachReportInputElement.createdTs,
                        modified_Ts: eachReportInputElement.modifiedTs,
                        deleted_Ts: eachReportInputElement.deletedTs,
                        global_designator: eachReportInputElement.globalDesignator,
                        in_page_input: eachReportInputElement.inPageInput,
                        parentId: eachReportInputElement.parentId,
                        defaultValue: eachReportInputElement.defaultValue
                    }
                }
            ),

            audReportPages: rDetailsReportPages.map(
                (eachReportPageElement) => {
                    return {
                        id: eachReportPageElement.id,
                        report_id: eachReportPageElement.reportId,
                        pageNum: eachReportPageElement.pageNum,
                        pageTitle: eachReportPageElement.pageTitle,
                        layout: eachReportPageElement.layout,
                        pdf_layout: eachReportPageElement.pdfLayout,
                        hide: eachReportPageElement.hide,

                        audReportItems: eachReportPageElement.reportItems.map(
                            (eachReportItemElement) => {
                                return {
                                    id: eachReportItemElement.id,
                                    page_Id: eachReportItemElement.pageId,
                                    dataSet_Id: eachReportItemElement.dataSetId,
                                    chart_Id: eachReportItemElement.chartId,
                                    tenant_Id: eachReportItemElement.tenantId,
                                    refreshRate: eachReportItemElement.refreshRate,
                                    created_By: eachReportItemElement.createdBy,
                                    modified_By: eachReportItemElement.modifiedBy,
                                    deleted_By: eachReportItemElement.deletedBy,
                                    created_Ts: eachReportItemElement.createdTs,
                                    modified_Ts: eachReportItemElement.modifiedTs,
                                    deleted_Ts: eachReportItemElement.deletedTs,
                                    hideTitle: eachReportItemElement.hideTitle,

                                    audReportItemCustomizations: eachReportItemElement.reportItemCustomizations.map(
                                        (eachReportItemCustomizationElement) => {
                                            return {
                                                id: eachReportItemCustomizationElement.id,
                                                report_item_Id: eachReportItemCustomizationElement.reportItemID,
                                                user_Id: eachReportItemCustomizationElement.userID,
                                                columnName: eachReportItemCustomizationElement.columnName,
                                                type: eachReportItemCustomizationElement.type,
                                                value: eachReportItemCustomizationElement.value,
                                                value_str: eachReportItemCustomizationElement.valueStr
                                            }
                                        }
                                    ),

                                    audReportItemInputMaps: eachReportItemElement.reportItemInputMaps.map(
                                        (eachReportItemInputMapElement) => {
                                            return {
                                                id: eachReportItemInputMapElement.id,
                                                report_input_Id: eachReportItemInputMapElement.reportInputID,
                                                dataSet_input_Id: eachReportItemInputMapElement.dataSetInputID,
                                                report_item_Id: eachReportItemInputMapElement.reportItemID
                                            }
                                        }
                                    ),
                                }
                            }
                        ),
                    }
                }
            ),

            operation: getROperation()
        };
    };

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-report-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_REPORT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_REPORT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManageActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for Report Explain.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = 'Report Explain';

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase.toLowerCase()) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase.toLowerCase())
        );
    };

    const { mutate: getAIContextAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, reportID, prompt }: GetAIReportExplainModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AIReportExplain`;
                const requestData: GetAIReportExplainModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    reportID: reportID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAIExplainModelResponse, GetAIReportExplainModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_REPORT_AI_CONTEXT_ASSIST: [AI Report Explain] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIExplainModelResponse) => {
                setAIContextAssistResultText((response.text ?? '').trim());
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI Report Explain returned successfully.' : 'AI Report Explain request did not succeed.',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_REPORT_AI_CONTEXT_ASSIST: [AI Report Explain] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'Unable to complete the AI Report Explain request.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const { data: reports = [], isLoading: isGetReportsLoading, refetch: refetchReports, } = useQuery<GetReportAdminModelsWithChildrenResponse[], Error, ManagedReport[]>(
        ['getReportAdminModelsWithChildrenRequest', rIncludeDisabled],
        async (): Promise<GetReportAdminModelsWithChildrenResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_ADMIN_MODELS_WITH_CHILDREN}`;
                const requestData: GetReportAdminModelsWithChildrenRequest = { grac_includeDisabled: rIncludeDisabled };
                return await AxiosUtil.get<GetReportAdminModelsWithChildrenResponse[], GetReportAdminModelsWithChildrenRequest>(url, requestData);
            } catch (err) {
                console.error('GET_REPORT_ADMIN_MODELS: [Get Report Admin Models AUD t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                console.error('GET_REPORT_ADMIN_MODELS: [Get Report Admin Models AUD o/e] Internal Error: ', err);
                showRInfoModal('Report Load Error', buildRErrorMessage('Unable to load Reports for Manage Reports.', err));
            },
            select: (response: GetReportAdminModelsWithChildrenResponse[]): ManagedReport[] =>
                response
                    .map(mapReportAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name)),
        },
    );

    const { mutate: audReport, isLoading: isAUDReportLoading, reset: resetAUDReport } = useMutation<boolean, Error, void>(
        async (): Promise<boolean> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.AUD_REPORT_DETAILS_BY_ID}`;
                const requestData: AUDReportRequest = buildAUDReportRequest();
                const responseData = await AxiosUtil.post<boolean, AUDReportRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('AUD_REPORT_DETAILS_BY_ID: [AUD Report t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                console.error('AUD_REPORT_DETAILS_BY_ID: [AUD Report o/e] Internal Error: ', err);
                showRResultModal(true, buildRErrorMessage(`${getRActionLabel()} Report: ${getRActionTargetName()} Failed. Please Contact Application Support`, err));
            },
            onSuccess: async (response: boolean) => {
                const reportName = getRActionTargetName();
                const actionLabel = getRActionLabel();

                invalidateReportMenu(queryClient);

                if (delButtonClicked) {
                    clearRSelection();
                    await refetchReports();
                    showRResultModal(false, `${actionLabel} Report: ${reportName} Succeeded.`);
                    return;
                }

                const refetchResult = await refetchReports();
                const refreshedReport = refetchResult.data?.find((eachReport) => eachReport.id === rSelectedReportID);

                if (refreshedReport) {
                    setRFormFromSelectedReport(refreshedReport);
                }

                showRResultModal(false, `${actionLabel} Report: ${reportName} Succeeded.`);
            },
        }
    );

    const getReportSecuritiesEditorContext = () => {
        const selectedReport = getSelectedReport();

        return {
            reportID: rSelectedReportID > 0 ? rSelectedReportID : 0,
            reportName: formAUDReports.getFieldValue('rDetailsName') ?? selectedReport?.name ?? '',
            reportGroupName: formAUDReports.getFieldValue('rDetailsGroupName') ?? selectedReport?.groupName ?? '',
            reportSecurities: rDetailsReportSecurities,
            initialSelectedReportSecurityID: rSelectedReportSecurityID,
            initialMode: rReportSecurityEditorMode,
        };
    };

    const openRReportSecuritiesEditor = (
        mode: ManagedAdminChildOperation,
        selectedReportSecurityID: number = -1
    ) => {
        setRReportSecurityEditorMode(mode);
        setRSelectedReportSecurityID(selectedReportSecurityID);
        setManageReportSecuritiesModalOpen(true);
    };

    const closeRReportSecuritiesEditor = () => {
        setManageReportSecuritiesModalOpen(false);
        setRReportSecurityEditorMode('idle');
        setRSelectedReportSecurityID(-1);
    };

    const applyReportSecurityEditResult = (
        existingReportSecurities: ManagedReportSecurity[],
        result: ManagedReportSecurityEditResult
    ): ManagedReportSecurity[] => {
        const { operation, reportSecurity } = result;

        switch (operation) {
            case 'add':
                return [...existingReportSecurities, reportSecurity];

            case 'update':
                return existingReportSecurities.map((eachReportSecurity) =>
                    eachReportSecurity.id === reportSecurity.id ? reportSecurity : eachReportSecurity
                );

            case 'delete':
                return existingReportSecurities.filter(
                    (eachReportSecurity) => eachReportSecurity.id !== reportSecurity.id
                );

            default:
                return existingReportSecurities;
        }
    };

    const handleSetReportSecurities = (result: ManagedReportSecurityEditResult) => {
        setRDetailsReportSecurities((currentReportSecurities) =>
            applyReportSecurityEditResult(currentReportSecurities, result)
        );
    };

    const onRSecurities = () => {
        openRReportSecuritiesEditor('add');
    };

    const getReportInputsEditorContext = () => {
        const selectedReport = getSelectedReport();

        return {
            reportID: rSelectedReportID > 0 ? rSelectedReportID : 0,
            reportName: formAUDReports.getFieldValue('rDetailsName') ?? selectedReport?.name ?? '',
            reportGroupName: formAUDReports.getFieldValue('rDetailsGroupName') ?? selectedReport?.groupName ?? '',
            reportInputs: rDetailsReportInputs,
            initialSelectedReportInputID: rSelectedReportInputID,
            initialMode: rReportInputEditorMode,
        };
    };

    const openRReportInputsEditor = (
        mode: ManagedAdminChildOperation,
        selectedReportInputID: number = -1
    ) => {
        setRReportInputEditorMode(mode);
        setRSelectedReportInputID(selectedReportInputID);
        setManageReportInputsModalOpen(true);
    };

    const closeRReportInputsEditor = () => {
        setManageReportInputsModalOpen(false);
        setRReportInputEditorMode('idle');
        setRSelectedReportInputID(-1);
    };

    const applyReportInputEditResult = (
        existingReportInputs: ManagedReportInput[],
        result: ManagedReportInputEditResult
    ): ManagedReportInput[] => {
        const { operation, reportInput } = result;

        switch (operation) {
            case 'add':
                return [...existingReportInputs, reportInput];

            case 'update':
                return existingReportInputs.map((eachReportInput) =>
                    eachReportInput.id === reportInput.id ? reportInput : eachReportInput
                );

            case 'delete':
                return existingReportInputs.filter(
                    (eachReportInput) => eachReportInput.id !== reportInput.id
                );

            default:
                return existingReportInputs;
        }
    };

    const handleSetReportInputs = (result: ManagedReportInputEditResult) => {
        setRDetailsReportInputs((currentReportInputs) =>
            applyReportInputEditResult(currentReportInputs, result)
        );
    };

    const onRInputs = () => {
        openRReportInputsEditor('add');
    };

    const getReportPagesEditorContext = () => {
        const selectedReport = getSelectedReport();

        return {
            reportID: rSelectedReportID > 0 ? rSelectedReportID : 0,
            reportName: formAUDReports.getFieldValue('rDetailsName') ?? selectedReport?.name ?? '',
            reportGroupName: formAUDReports.getFieldValue('rDetailsGroupName') ?? selectedReport?.groupName ?? '',
            reportPages: rDetailsReportPages,
            initialSelectedReportPageID: rSelectedReportPageID,
            initialMode: rReportPageEditorMode,
        };
    };

    const openRReportPagesEditor = (
        mode: ManagedAdminChildOperation,
        selectedReportPageID: number = -1
    ) => {
        setRReportPageEditorMode(mode);
        setRSelectedReportPageID(selectedReportPageID);
        setManageReportPagesModalOpen(true);
    };

    const closeRReportPagesEditor = () => {
        setManageReportPagesModalOpen(false);
        setRReportPageEditorMode('idle');
        setRSelectedReportPageID(-1);
    };

    const applyReportPageEditResult = (
        existingReportPages: ManagedReportPage[],
        result: ManagedReportPageEditResult
    ): ManagedReportPage[] => {
        const { operation, reportPage } = result;

        switch (operation) {
            case 'add':
                return [...existingReportPages, reportPage];

            case 'update':
                return existingReportPages.map((eachReportPage) =>
                    eachReportPage.id === reportPage.id ? reportPage : eachReportPage
                );

            case 'delete':
                return existingReportPages.filter(
                    (eachReportPage) => eachReportPage.id !== reportPage.id
                );

            default:
                return existingReportPages;
        }
    };

    const handleSetReportPages = (result: ManagedReportPageEditResult) => {
        setRDetailsReportPages((currentReportPages) =>
            applyReportPageEditResult(currentReportPages, result)
        );
    };

    const onRPages = () => {
        openRReportPagesEditor('add');
    };

    const onFinishFailed = (_errorInfo: ManagedFormErrorInfo) => {
        showRInfoModal('Report Validation Error', 'Please correct the highlighted Report fields and try again.');
    };

    const onFinish = () => {
        audReport();
    };

    const buildReportExplainSelectionSummary = (report: ManagedReport | undefined): string => {
        if (!report) {
            return '';
        }

        return [
            `Report ID: ${report.id}`,
            `Report Name: ${report.name}`,
            `Report Group: ${report.groupName}`,
            '',
            'The detailed Report Explain context is built by the backend.',
        ].join('\n');
    };

    const getSelectedReport = (selectedReportID: number = rSelectedReportID): ManagedReport | undefined => {
        return reports.find((report) => report.id === selectedReportID);
    };

    const handleOpenAIReportExplainModal = () => {
        const selectedReport = getSelectedReport();

        setAIContextAssistCurrentText(buildReportExplainSelectionSummary(selectedReport));
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                'Unable to find an AI Provider matching Report Explain.',
            ]);
            return;
        }

        if (rSelectedReportID <= 0) {
            setAIContextAssistAncillaryItems([
                'Select a Report before asking AI to explain it.',
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        getAIContextAssistPrompt({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            reportID: rSelectedReportID,
            prompt: requestText,
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const handleRSelectionChange = (value: number) => {
        const selectedReport = getSelectedReport(value);

        setRSelectedReportID(value);
        setRSelectedReportName(selectedReport?.name ?? '');

        setRSelectedReportCreatedBy(selectedReport?.createdBy ?? 0);
        setRSelectedReportCreatedTs(selectedReport?.createdTs ?? new Date());
        setRSelectedReportDeletedBy(selectedReport?.deletedBy ?? 0);
        setRSelectedReportDeletedTs(selectedReport?.deletedTs ?? new Date());

        setRMode('add');
        setRBlankForm();
    };

    const setRBlankForm = () => {
        formAUDReports.setFieldValue('rDetailsName', '');
        formAUDReports.setFieldValue('rDetailsGroupName', '');
        formAUDReports.setFieldValue('rDetailsIsDashboard', false);
        formAUDReports.setFieldValue('rDetailsUseDatasetInputs', true);
        formAUDReports.setFieldValue('rDetailsReleased', false);
        formAUDReports.setFieldValue('rDetailsNameGeneric', '');
        formAUDReports.setFieldValue('rDetailsGroupNameGeneric', '');
        formAUDReports.setFieldValue('rDetailsSort', 0);
        formAUDReports.setFieldValue('rDetailsAPIOnly', false);
        formAUDReports.setFieldValue('rDetailsExportExcel', true);
        formAUDReports.setFieldValue('rDetailsExportPDF', false);
        formAUDReports.setFieldValue('rDetailsExportPassword', '');

        setRDetailsName('');
        setRDetailsGroupName('');
        setRDetailsNameGeneric('');
        setRDetailsGroupNameGeneric('');

        setRDetailsReportSecurities([]);
        setRDetailsReportInputs([]);
        setRDetailsReportPages([]);
    };

    const setRFormFromSelectedReport = (reportOverride?: ManagedReport) => {
        const selectedReport = reportOverride ?? getSelectedReport();

        formAUDReports.setFieldValue('rDetailsName', selectedReport?.name ?? '');
        formAUDReports.setFieldValue('rDetailsGroupName', selectedReport?.groupName ?? '');
        formAUDReports.setFieldValue('rDetailsIsDashboard', selectedReport?.isDashboard ?? false);
        formAUDReports.setFieldValue('rDetailsUseDatasetInputs', selectedReport?.useDatasetInputs ?? true);
        formAUDReports.setFieldValue('rDetailsReleased', selectedReport?.released ?? false);
        formAUDReports.setFieldValue('rDetailsNameGeneric', selectedReport?.nameGeneric ?? '');
        formAUDReports.setFieldValue('rDetailsGroupNameGeneric', selectedReport?.groupNameGeneric ?? '');
        formAUDReports.setFieldValue('rDetailsSort', selectedReport?.sort ?? 0);
        formAUDReports.setFieldValue('rDetailsAPIOnly', selectedReport?.apiOnly ?? false);
        formAUDReports.setFieldValue('rDetailsExportExcel', selectedReport?.exportExcel ?? true);
        formAUDReports.setFieldValue('rDetailsExportPDF', selectedReport?.exportPDF ?? false);
        formAUDReports.setFieldValue('rDetailsExportPassword', selectedReport?.exportPassword ?? '');

        setRDetailsName(selectedReport?.name ?? '');
        setRDetailsGroupName(selectedReport?.groupName ?? '');
        setRDetailsNameGeneric(selectedReport?.nameGeneric ?? '');
        setRDetailsGroupNameGeneric(selectedReport?.groupNameGeneric ?? '');

        setRDetailsReportSecurities(selectedReport?.reportSecurities ?? []);
        setRDetailsReportInputs(selectedReport?.reportInputs ?? []);
        setRDetailsReportPages(selectedReport?.reportPages ?? []);
    };

    const handleRReportResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setRBlankForm();
            return;
        }

        setRFormFromSelectedReport();
    };

    const handleRDetailsNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRDetailsName(value);

        if (addButtonClicked && ((rDetailsNameGeneric === '') || (rDetailsNameGeneric === rDetailsName))) {
            setRDetailsNameGeneric(value);
            formAUDReports.setFieldValue('rDetailsNameGeneric', value);
        }
    };

    const handleRDetailsNameGenericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRDetailsNameGeneric(value);
    };

    const handleRDetailsGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRDetailsGroupName(value);

        if (addButtonClicked && ((rDetailsGroupNameGeneric === '') || (rDetailsGroupNameGeneric === rDetailsGroupName))) {
            setRDetailsGroupNameGeneric(value);
            formAUDReports.setFieldValue('rDetailsGroupNameGeneric', value);
        } 
    };

    const handleRDetailsGroupNameGenericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRDetailsGroupNameGeneric(value);
    };

    //=================================================================
    //=================================================================
    
    const handleRAddReport = () => {
        resetAUDReport();
        clearRSelection();
    };

    //=================================================================
    //=================================================================

    const handleRUpdReport = () => {
        resetAUDReport();
        setRMode('update');
        handleRReportResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleRDelReport = () => {
        resetAUDReport();
        setRMode('delete');
        handleRReportResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleManageModalOk = () => {
        setManageActionModalState((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const onRCancel = () => {
        resetAUDReport();
        clearRSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '8px',
        height: '470px'
    };
    
    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageModalOk}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title='AI Report Explain'
                currentSectionTitle='Current Report Context'
                requestSectionTitle='What do you want explained?'
                resultSectionTitle='Report Explanation'
                ancillarySectionTitle='Ancillary Information'
                currentText={aiContextAssistCurrentText}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                requestPlaceholder='Optionally describe what part of this Report you want explained.'
                askButtonText='Explain Report'
                useButtonText='Use Suggestion'
                loading={aiContextAssistLoading || isGetAIProvidersLoading}
                allowUseSuggestion={false}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={() => undefined}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageReportSecuritiesModalOpen}
                onCancel={closeRReportSecuritiesEditor}
            >
                {
                    manageReportSecuritiesModalOpen &&
                    (
                        <AUDReportSecurities
                            reportID={getReportSecuritiesEditorContext().reportID}
                            reportName={getReportSecuritiesEditorContext().reportName}
                            reportGroupName={getReportSecuritiesEditorContext().reportGroupName}
                            reportSecurities={getReportSecuritiesEditorContext().reportSecurities}
                            initialSelectedReportSecurityID={getReportSecuritiesEditorContext().initialSelectedReportSecurityID}
                            initialMode={getReportSecuritiesEditorContext().initialMode}
                            handleSetReportSecurities={handleSetReportSecurities}
                            close={closeRReportSecuritiesEditor}
                        />
                    )
                }
            </ServaModal>
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageReportInputsModalOpen}
                onCancel={closeRReportInputsEditor}
            >
                {
                    manageReportInputsModalOpen &&
                    (
                        <AUDReportInputs
                            reportID={getReportInputsEditorContext().reportID}
                            reportName={getReportInputsEditorContext().reportName}
                            reportGroupName={getReportInputsEditorContext().reportGroupName}
                            reportInputs={getReportInputsEditorContext().reportInputs}
                            initialSelectedReportInputID={getReportInputsEditorContext().initialSelectedReportInputID}
                            initialMode={getReportInputsEditorContext().initialMode}
                            handleSetReportInputs={handleSetReportInputs}
                            close={closeRReportInputsEditor}
                        />
                    )
                }
            </ServaModal>
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageReportPagesModalOpen}
                onCancel={closeRReportPagesEditor}
            >
                {
                    manageReportPagesModalOpen &&
                    (
                        <AUDReportPages
                            reportID={getReportPagesEditorContext().reportID}
                            reportName={getReportPagesEditorContext().reportName}
                            reportGroupName={getReportPagesEditorContext().reportGroupName}
                            reportPages={getReportPagesEditorContext().reportPages}
                            initialSelectedReportPageID={getReportPagesEditorContext().initialSelectedReportPageID}
                            initialMode={getReportPagesEditorContext().initialMode}
                            handleSetReportPages={handleSetReportPages}
                            close={closeRReportPagesEditor}
                        />
                    )
                }
            </ServaModal>
            <Spin spinning={
                            isGetReportsLoading ||
                            isAUDReportLoading
                           }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Reports</Title>
                    </Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{ float: 'right', paddingTop: '8px' }}>Select Report to Update or Delete:</Text>
                    </Col>
                    <Col span={10}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Report'}
                            showSearch
                            style={{paddingTop: '4px', width: '100%'}}
                            value={rSelectedReportID === -1 ? undefined : rSelectedReportID}
                            onChange={handleRSelectionChange}
                        >
                            {reports.map(
                                (eachReport) => {
                                    return (
                                        <Option
                                            key={eachReport.id}
                                            label={'[' + eachReport.groupName + '] ' + eachReport.name}
                                            value={eachReport.id}
                                        >
                                            {'[' + eachReport.groupName + '] ' + eachReport.name}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                    <Col span={1}>
                        <Button
                            disabled={rSelectedReportID === -1 || isGetAIProvidersLoading}
                            icon={<BulbOutlinedIlluminated />}
                            style={{minWidth: '32px', padding: '2px',}}
                            title='AI Report Explain'
                            type='default'
                            onClick={handleOpenAIReportExplainModal}
                        />
                    </Col>
                    <Col span={3} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={3}>
                        <Button
                            icon={<AddIcon />}
                            style={{width: '95%', marginBottom: '4px', float: 'left',}}
                            type='primary'
                            onClick={handleRAddReport}
                        >
                            Add Report
                        </Button>
                    </Col>
                    <Col span={9} />
                    <Col span={3}>
                        <Button
                            disabled={rSelectedReportID === -1}
                            icon={<EditIcon />}
                            style={{width: '95%', backgroundColor: 'darkgray', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleRUpdReport}
                        >
                            Update Report
                        </Button>
                    </Col>
                    <Col span={3}>
                        <Button
                            disabled={rSelectedReportID === -1}
                            icon={<DeleteIcon />}
                            style={{width: '95%', backgroundColor: 'red', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleRDelReport}
                        >
                            Delete Report
                        </Button>
                    </Col>
                    <Col span={4} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAUDReports}
                    key='audReports'
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div style={contentStyle}>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getRActionColor() }}>
                                            {addButtonClicked ? 'Add Report' :
                                                (updButtonClicked ? 'Update Report: [' + formatRDisplayID(rSelectedReportID) + '] ' + rSelectedReportName :
                                                    (delButtonClicked ? 'Delete Report: [' + formatRDisplayID(rSelectedReportID) + '] ' + rSelectedReportName : ''))}
                                        </Divider>
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button
                                            style={{ color: getRActionColor(), fontStyle: 'italic' }}
                                            type='link'
                                            onClick={() => handleRReportResetForm(addButtonClicked ? 'blank' : 'selected')}
                                        >
                                            Reset Form
                                        </Button>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='rdetailsname'
                                            name='rDetailsName'
                                            label='Name'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Name required.',
                                                    required: true,
                                                    validator: validateRDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                                onChange={handleRDetailsNameChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            key='rdetailsgroupname'
                                            name='rDetailsGroupName'
                                            label='Group Name'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Group Name required.',
                                                    required: true,
                                                    validator: validateRDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Group Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                                onChange={handleRDetailsGroupNameChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='rdetailsnamegeneric'
                                            name='rDetailsNameGeneric'
                                            label='Name Generic'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateRDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Name Generic auto-applied'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                                onChange={handleRDetailsNameGenericChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            key='rdetailsgroupnamegeneric'
                                            name='rDetailsGroupNameGeneric'
                                            label='Group Name Generic'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateRDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Group Name Generic auto-applied'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                                onChange={handleRDetailsGroupNameGenericChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailsisdashboard'
                                            name='rDetailsIsDashboard'
                                            valuePropName='checked'
                                            style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                            >
                                                Is Dashboard
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailsusedatasetinputs'
                                            name='rDetailsUseDatasetInputs'
                                            valuePropName='checked'
                                            style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                            >
                                                Use Dataset Inputs
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailsreleased'
                                            name='rDetailsReleased'
                                            valuePropName='checked'
                                            style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                            >
                                                Released
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailsapionly'
                                            name='rDetailsAPIOnly'
                                            valuePropName='checked'
                                            style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                            >
                                                API Only
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailsexportexcel'
                                            name='rDetailsExportExcel'
                                            valuePropName='checked'
                                            style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                            >
                                                Export Excel
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailsexportpdf'
                                            name='rDetailsExportPDF'
                                            valuePropName='checked'
                                            style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                            >
                                                Export PDF
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='rdetailssort'
                                            name='rDetailsSort'
                                            label='Sort'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateRDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Sort'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '40%'}}
                                                type='number'
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8} />
                                    <Col span={7}>
                                        <Form.Item
                                            key='rdetailsexportpassword'
                                            name='rDetailsExportPassword'
                                            label='Export Password'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateRDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Export Password'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '4px' }}>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailssecurities'
                                            name='rDetailsSecurities'
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onRSecurities}
                                            >
                                                Report Securities
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailsinputs'
                                            name='rDetailsInputs'
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onRInputs}
                                            >
                                                Report Inputs
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='rdetailspages'
                                            name='rDetailsPages'
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onRPages}
                                            >
                                                Report Pages
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ visibility: addButtonClicked ? 'hidden' : 'visible' }}>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                                            <Text italic style={{ color: 'gray' }}>
                                                NOTE: Reports may be linked directly (via Foreign Key) to Report Securities, Report Inputs, and Report Pages.
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={17} />
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='rApplyButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType='submit'
                                        style={{width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                    >
                                        {addButtonClicked ? 'Add Report' : (updButtonClicked ? 'Update Report' : (delButtonClicked ? 'Delete Report' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='rCancelButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        style={{width: '95%'}}
                                        type='default'
                                        onClick={onRCancel}
                                    >
                                        {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                </Form>
            </Spin>
        </Card>
    );
};

export default AUDReport;
